// src/components/admin/bespokeai/CommunityAI.jsx
import React, { useState, useRef, useEffect } from 'react';
import { auth } from '../../../utils/firebase';
import { fetchCommunityContext } from '../../../api/aiContextApi';
import DailyTokenCounter from './DailyTokenCounter';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const PRESET_PROMPTS = [
  'Identify members at risk of disengagement',
  'Suggest outreach strategy for at-risk members',
  'Write a volunteer recruitment message',
  'Summarise this month\'s volunteer performance',
  'Suggest recognition messages for top volunteers',
];

const INITIAL_MESSAGE = {
  id: 'init',
  role: 'assistant',
  type: 'ai',
  content: "Hello! I'm BespokeAI, your intelligent assistant for community management. I can help you identify at-risk members, craft volunteer outreach messages, and generate recognition content. How can I help you today?",
};

export default function CommunityAI({ orgId, adminId }) {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const counterRef = useRef(null);
  const [copied,   setCopied]   = useState(null);
  const chatRef    = useRef(null);
  const prevLenRef = useRef(1);

  useEffect(() => {
    const prev = prevLenRef.current;
    prevLenRef.current = messages.length;
    if (messages.length > prev && prev > 0 && chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading || limitReached) return;

    const updatedMessages = [...messages, { id: Date.now(), role: 'user', type: 'user', content: trimmed }];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const [token, context] = await Promise.all([
        auth.currentUser.getIdToken(),
        fetchCommunityContext(orgId),
      ]);

      const apiMessages = updatedMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ messages: apiMessages, module: 'community', context }),
      });
      const data = await res.json();
      if (data.dailyUsage) counterRef.current?.update(data.dailyUsage);
      const reply = data.success ? data.text : (data.message || 'Something went wrong. Please try again.');

      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'ai', content: reply, showCopy: true }]);
    } catch (err) {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'ai', content: `Error: ${err.message}`, showCopy: false }]);
    }
    setLoading(false);
  };

  const handleSend = () => sendMessage(input);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleCopy = (id, content) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="bb-ai-container">
      <div className="bb-ai-header">
        <h3>BespokeAI — Community</h3>
        <p>AI-powered member insights, volunteer recognition, and outreach copy</p>
      </div>

      <DailyTokenCounter ref={counterRef} orgId={orgId} onLimitReached={setLimitReached} />

      <div className="bb-ai-messages" ref={chatRef}>
        {messages.map(msg => (
          <div key={msg.id} className={`bb-ai-msg bb-ai-msg-${msg.type}`}>
            <div className={`bb-ai-bubble-${msg.type === 'user' ? 'user' : 'ai'}`}>
              <pre>{msg.content}</pre>
            </div>
            {msg.type === 'ai' && msg.showCopy && (
              <div className="bb-ai-actions">
                <button className="bb-ai-action-btn" onClick={() => handleCopy(msg.id, msg.content)}>
                  {copied === msg.id ? 'Copied!' : 'Copy Text'}
                </button>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="bb-ai-msg bb-ai-msg-ai">
            <div className="bb-ai-bubble-ai" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ opacity: 0.5 }}>● ● ●</span>
            </div>
          </div>
        )}
      </div>

      <div className="bb-ai-presets">
        {PRESET_PROMPTS.map((p, i) => (
          <button key={i} className="bb-ai-preset-chip" onClick={() => sendMessage(p)} disabled={loading || limitReached}>
            {p}
          </button>
        ))}
      </div>

      <div className="bb-ai-input-row">
        <textarea
          className="bb-ai-input"
          placeholder={limitReached ? 'Daily AI usage limit reached. Resets at 00:00:01.' : "Ask BespokeAI about your community members or volunteers..."}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={limitReached}
          rows={1}
        />
        <button className="bb-ai-send-btn" onClick={handleSend} disabled={loading || !input.trim() || limitReached}>
          Send
        </button>
      </div>
    </div>
  );
}
